import React, { useEffect, useState } from 'react';
import { Form, InputNumber, Select, Button, Spin, Typography } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import {
  useUpdateOneGraphicsSettingsMutation,
  useUpdateOnePostProcessingSettingsMutation,
  useFindFirstGraphicsSettingsQuery,
  useFindFirstPostProcessingSettingsQuery,
  useCreateOneGraphicsSettingsMutation,
  useFindFirstCloudConfigQuery,
  useUpdateOneCloudConfigMutation,
  useFindFirstFogConfigQuery,
  useUpdateOneFogConfigMutation,
  CloudConfig,
  FogConfig,
} from '../../generated/graphql';
import { openNotification } from 'util/helpers';
import { LoadingOutlined } from '@ant-design/icons';
import { useRouter } from 'next/router';
import styles from './graphics-settings.module.css';
import CameraConfig from './CameraConfig';
import CloudSettings from './CloudSettings';
import FogSettings from './FogSettings';

const notify = (type, msg, desc) => {
  openNotification(msg, desc, 'top', 2, type);
};

const { Option } = Select;

const GraphicsSettings = ({ cameraConfigProps }) => {
  const [disableBtn, setDisableBtn] = useState(true);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isBloomActive, setIsBloomActive] = useState<boolean>(false);
  const [isSSRActive, setIsSSRActive] = useState<boolean>(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [cloudConfig, setCloudConfig] = useState<CloudConfig | any>(null);
  const [fogConfig, setFogConfig] = useState<any>(null);
  const [updateGraphicsSettings] = useUpdateOneGraphicsSettingsMutation();
  const [updatePostprocessingSettings] =
    useUpdateOnePostProcessingSettingsMutation();
  const [createOneGraphicsSettings] = useCreateOneGraphicsSettingsMutation();
  const [updateCloudConfig] = useUpdateOneCloudConfigMutation();
  const [updateFogConfig] = useUpdateOneFogConfigMutation();

  const { currentCameraConfig, callBack, type, callBackSave } =
    cameraConfigProps;

  const router = useRouter();
  const sceneId = Array.isArray(router.query?.id)
    ? router.query?.id[0]
    : router.query?.id;

  const { data, loading, error } = useFindFirstGraphicsSettingsQuery({
    variables: {
      where: {
        UserScene: {
          some: {
            id: {
              equals: sceneId,
            },
          },
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const {
    data: dataPP,
    loading: loadingPP,
    error: errorPP,
  } = useFindFirstPostProcessingSettingsQuery({
    variables: {
      where: {
        id: {
          equals: data?.findFirstGraphicsSettings?.postProcessingSettingsId,
        },
      },
    },
    skip: !data?.findFirstGraphicsSettings?.postProcessingSettingsId,
    fetchPolicy: 'no-cache',
  });

  const {
    data: dataCC,
    loading: loadingCC,
    error: errorCC,
  } = useFindFirstCloudConfigQuery({
    variables: {
      where: {
        id: {
          equals: data?.findFirstGraphicsSettings?.cloudConfigId,
        },
      },
    },
    skip: !data?.findFirstGraphicsSettings?.cloudConfigId,
    fetchPolicy: 'no-cache',
  });

  const {
    data: dataFC,
    loading: loadingFC,
    error: errorFC,
  } = useFindFirstFogConfigQuery({
    variables: {
      where: {
        id: {
          equals: data?.findFirstGraphicsSettings?.fogConfigId,
        },
      },
    },
    skip: !data?.findFirstGraphicsSettings?.fogConfigId,
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (loading || error || loadingPP || errorPP) {
      return;
    }
    if (!data?.findFirstGraphicsSettings?.id) {
      createOneGraphicsSettings({
        variables: {
          data: {
            preset: 'medium',
            postProcessingSettings: {
              create: {
                antialiasing: 'off',
                bloom: 'off',
                ssr: 'off',
                ssao: 'off',
                bloomExposure: 0.75,
                bloomRadius: 0.5,
                bloomStrength: 0.75,
                bloomThreshold: 0.3,
                ssrOpacity: 0.1,
                ssrWidth: 960,
                ssrHeight: 540,
                ssrMaxDistance: 8,
              },
            },
            cloudConfig: {
              create: {
                enabled: false,
                count: 16,
                speed: 1.4,
                scaleX: 408,
                scaleY: 119,
                scaleZ: 408,
                opacity: 0.25,
                colorHex: '#798aa0',
                windDirectionX: 1,
                windDirectionY: 0,
                windDirectionZ: 0.2,
                spawnAreaMinX: -600,
                spawnAreaMinY: 360,
                spawnAreaMinZ: -600,
                spawnAreaMaxX: 600,
                spawnAreaMaxY: 480,
                spawnAreaMaxZ: 600,
                threshold: 0.25,
                range: 0.1,
                steps: 100,
                variationOpacity: 0.15,
                variationScale: 0.4,
                variationThreshold: 0,
                variationColor: 0.5,
              },
            },
            UserScene: {
              connect: [
                {
                  id: sceneId,
                },
              ],
            },
          },
        },
        refetchQueries: [
          'FindFirstPostProcessingSettings',
          'FindFirstGraphicsSettings',
          'FindFirstCloudConfig',
        ],
      });
    }
  }, [data?.findFirstGraphicsSettings]);

  useEffect(() => {
    if (
      !loading &&
      !error &&
      !loadingPP &&
      !errorPP &&
      dataPP?.findFirstPostProcessingSettings
    ) {
      setIsBloomActive(dataPP.findFirstPostProcessingSettings.bloom === 'on');
      setIsSSRActive(dataPP.findFirstPostProcessingSettings.ssr === 'on');
    }
  }, [dataPP, loading, error, loadingPP, errorPP]);

  useEffect(() => {
    if (
      !loading &&
      !error &&
      !loadingCC &&
      !errorCC &&
      dataCC?.findFirstCloudConfig
    ) {
      setCloudConfig(dataCC.findFirstCloudConfig);
    }
  }, [dataCC, loading, error, loadingCC, errorCC]);

  useEffect(() => {
    if (
      !loading &&
      !error &&
      !loadingFC &&
      !errorFC &&
      dataFC?.findFirstFogConfig
    ) {
      setFogConfig(dataFC.findFirstFogConfig);
    }
  }, [dataFC, loading, error, loadingFC, errorFC]);

  const handleCloudConfigChange = ({ type, value, key }) => {
    setCloudConfig((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleFogConfigChange = ({ type, value, key }) => {
    setFogConfig((prev: any) => ({
      ...prev,
      [key]: value,
    }));
    setDisableBtn(false);
  };

  if (loading || error || loadingPP || errorPP) {
    return <></>;
  }

  const graphicsSettingsData = data?.findFirstGraphicsSettings;
  const postprocessingSettingsData = dataPP?.findFirstPostProcessingSettings;

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions(!showAdvancedOptions);
  };

  async function onFinish(values) {
    setLoadingUpdate(true);
    const {
      bloom,
      bloomExposure,
      bloomRadius,
      bloomStrength,
      bloomThreshold,
      ssrWidth,
      ssrHeight,
      ssrMaxDistance,
      ssrOpacity,
      antialiasing,
      ssr,
      ssao,
    } = values;
    let resPP, res, resCC, resFC;
    try {
      res = await updateGraphicsSettings({
        variables: {
          data: {
            physicsEngine: {
              set: values.physics,
            },
          },
          where: {
            id: graphicsSettingsData?.id,
          },
        },
      });
      resPP = await updatePostprocessingSettings({
        variables: {
          where: {
            id: postprocessingSettingsData?.id,
          },
          data: {
            bloom: {
              set: bloom,
            },
            bloomExposure:
              bloomExposure !== undefined
                ? {
                    set: bloomExposure,
                  }
                : undefined,
            bloomRadius:
              bloomRadius !== undefined
                ? {
                    set: bloomRadius,
                  }
                : undefined,
            bloomStrength:
              bloomStrength !== undefined
                ? {
                    set: bloomStrength,
                  }
                : undefined,
            bloomThreshold:
              bloomThreshold !== undefined
                ? {
                    set: bloomThreshold,
                  }
                : undefined,
            antialiasing: {
              set: antialiasing,
            },
            ssr: {
              set: ssr,
            },
            ssrWidth:
              ssrWidth !== undefined
                ? {
                    set: ssrWidth,
                  }
                : undefined,
            ssrHeight:
              ssrHeight !== undefined
                ? {
                    set: ssrHeight,
                  }
                : undefined,
            ssrMaxDistance:
              ssrMaxDistance !== undefined
                ? {
                    set: ssrMaxDistance,
                  }
                : undefined,
            ssrOpacity:
              ssrOpacity !== undefined
                ? {
                    set: ssrOpacity,
                  }
                : undefined,
            ssao: {
              set: ssao,
            },
          },
        },
      });

      if (dataCC?.findFirstCloudConfig?.id) {
        resCC = await updateCloudConfig({
          variables: {
            where: { id: dataCC.findFirstCloudConfig.id },
            data: {
              enabled: { set: cloudConfig.enabled },
              count: { set: cloudConfig.count },
              speed: { set: cloudConfig.speed },
              scaleX: { set: cloudConfig.scaleX },
              scaleY: { set: cloudConfig.scaleY },
              scaleZ: { set: cloudConfig.scaleZ },
              opacity: { set: cloudConfig.opacity },
              colorHex: { set: cloudConfig.colorHex },
              windDirectionX: { set: cloudConfig.windDirectionX },
              windDirectionY: { set: cloudConfig.windDirectionY },
              windDirectionZ: { set: cloudConfig.windDirectionZ },
              spawnAreaMinX: { set: cloudConfig.spawnAreaMinX },
              spawnAreaMinY: { set: cloudConfig.spawnAreaMinY },
              spawnAreaMinZ: { set: cloudConfig.spawnAreaMinZ },
              spawnAreaMaxX: { set: cloudConfig.spawnAreaMaxX },
              spawnAreaMaxY: { set: cloudConfig.spawnAreaMaxY },
              spawnAreaMaxZ: { set: cloudConfig.spawnAreaMaxZ },
              threshold: { set: cloudConfig.threshold },
              range: { set: cloudConfig.range },
              steps: { set: cloudConfig.steps },
              variationOpacity: { set: cloudConfig.variationOpacity },
              variationScale: { set: cloudConfig.variationScale },
              variationThreshold: { set: cloudConfig.variationThreshold },
              variationColor: { set: cloudConfig.variationColor },
            },
          },
        });
      }

      if (dataFC?.findFirstFogConfig?.id) {
        resFC = await updateFogConfig({
          variables: {
            where: { id: dataFC.findFirstFogConfig.id },
            data: {
              enabled: { set: fogConfig.enabled },
              type: { set: fogConfig.type },
              colorHex: { set: fogConfig.colorHex },
              near: { set: fogConfig.near },
              far: { set: fogConfig.far },
              density: { set: fogConfig.density },
              autoUpdate: { set: fogConfig.autoUpdate },
              preset: { set: fogConfig.preset },
            },
          },
        });
      }
    } catch (error) {
      console.log(error);
    }

    //camera config save callback
    callBackSave(sceneId);

    setLoadingUpdate(false);

    if (
      resPP?.data?.updateOnePostProcessingSettings?.id &&
      res.data?.updateOneGraphicsSettings?.id &&
      (!dataCC?.findFirstCloudConfig?.id ||
        resCC?.data?.updateOneCloudConfig?.id) &&
      (!dataFC?.findFirstFogConfig?.id || resFC?.data?.updateOneFogConfig?.id)
    ) {
      notify('success', 'Successfully', 'saved changes');
      setDisableBtn(true);
    } else {
      notify('error', 'Error', 'something went wrong please try again');
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.formContainer}>
        <Form
          key={`${graphicsSettingsData?.id}-${postprocessingSettingsData?.id}`}
          layout='vertical'
          onFinish={onFinish}
          onValuesChange={(values) => setDisableBtn(false)}
        >
          <Typography.Title level={4}>Graphics Settings</Typography.Title>

          <Form.Item
            label={
              <div>
                <Typography.Paragraph strong>Preset</Typography.Paragraph>
                <div className={styles.soonBadge}>Coming soon</div>
              </div>
            }
            name='preset'
            initialValue={graphicsSettingsData?.preset || 'medium'}
          >
            <Select placeholder='Medium' disabled>
              <Option value='low'>Low</Option>
              <Option value='medium'>Medium</Option>
              <Option value='high'>High</Option>
              <Option value='custom'>Custom</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={<Typography.Paragraph strong>Bloom</Typography.Paragraph>}
            name='bloom'
            initialValue={postprocessingSettingsData?.bloom || 'off'}
          >
            <Select
              placeholder='off'
              onChange={(val) => {
                setIsBloomActive(val === 'on');
              }}
            >
              <Option value='off'>off</Option>
              <Option value='on'>on</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={
              <div>
                <Typography.Paragraph strong>Antialiasing</Typography.Paragraph>
              </div>
            }
            name='antialiasing'
            initialValue={postprocessingSettingsData?.antialiasing || 'off'}
          >
            <Select placeholder='SMAA'>
              <Option value='SMAA'>SMAA</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={
              <div>
                <Typography.Paragraph strong>
                  Ambient Occlusion
                </Typography.Paragraph>
              </div>
            }
            name='ssao'
            initialValue={postprocessingSettingsData?.ssao || 'off'}
          >
            <Select placeholder='off'>
              <Option value='off'>off</Option>
              <Option value='on'>on</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={
              <div>
                <Typography.Paragraph strong>Reflections</Typography.Paragraph>
              </div>
            }
            name='ssr'
            initialValue={postprocessingSettingsData?.ssr || 'off'}
          >
            <Select
              placeholder='off'
              onChange={(val) => {
                setIsSSRActive(val === 'on');
              }}
            >
              <Option value='off'>off</Option>
              <Option value='on'>on</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={
              <Typography.Paragraph strong>Physics Engine</Typography.Paragraph>
            }
            name='physics'
            initialValue={graphicsSettingsData?.physicsEngine || 'off'}
          >
            <Select placeholder='off'>
              <Option value='off'>off</Option>
              <Option value='PhysX'>PhysX</Option>
            </Select>
          </Form.Item>

          <div className={styles.optionsToggle} onClick={toggleAdvancedOptions}>
            {showAdvancedOptions ? (
              <>
                <UpOutlined /> Hide Advanced Options
              </>
            ) : (
              <>
                <DownOutlined /> Show Advanced Options
              </>
            )}
          </div>

          {showAdvancedOptions && (
            <div className={styles.optionsContainer}>
              {isBloomActive && (
                <>
                  <Typography.Title level={5}>Bloom Config</Typography.Title>
                  <Form.Item
                    label={
                      <Typography.Paragraph strong>
                        Intensity
                      </Typography.Paragraph>
                    }
                    name='bloomStrength'
                    initialValue={
                      postprocessingSettingsData?.bloomStrength || 0.75
                    }
                  >
                    <InputNumber step={0.01} max={3} min={0} />
                  </Form.Item>

                  <Form.Item
                    label={
                      <Typography.Paragraph strong>
                        Exposure
                      </Typography.Paragraph>
                    }
                    name='bloomExposure'
                    initialValue={
                      postprocessingSettingsData?.bloomExposure || 0.75
                    }
                  >
                    <InputNumber step={0.01} max={2} min={0} />
                  </Form.Item>

                  <Form.Item
                    label={
                      <Typography.Paragraph strong>
                        Threshold
                      </Typography.Paragraph>
                    }
                    name='bloomThreshold'
                    initialValue={
                      postprocessingSettingsData?.bloomThreshold || 0.3
                    }
                  >
                    <InputNumber step={0.01} max={1} min={0} />
                  </Form.Item>

                  <Form.Item
                    label={
                      <Typography.Paragraph strong>Radius</Typography.Paragraph>
                    }
                    name='bloomRadius'
                    initialValue={postprocessingSettingsData?.bloomRadius || 0}
                  >
                    <InputNumber step={0.01} max={1} min={0} />
                  </Form.Item>
                </>
              )}

              {isSSRActive && (
                <>
                  <Typography.Title level={5}>SSR Config</Typography.Title>
                  <Form.Item
                    label={
                      <Typography.Paragraph strong>
                        Opacity
                      </Typography.Paragraph>
                    }
                    name='ssrOpacity'
                    initialValue={postprocessingSettingsData?.ssrOpacity || 0.1}
                  >
                    <InputNumber step={0.01} max={1} min={0} />
                  </Form.Item>

                  <Form.Item
                    label={
                      <Typography.Paragraph strong>Width</Typography.Paragraph>
                    }
                    name='ssrWidth'
                    initialValue={postprocessingSettingsData?.ssrWidth || 960}
                  >
                    <InputNumber step={1} max={8192} min={512} />
                  </Form.Item>

                  <Form.Item
                    label={
                      <Typography.Paragraph strong>Height</Typography.Paragraph>
                    }
                    name='ssrHeight'
                    initialValue={postprocessingSettingsData?.ssrHeight || 540}
                  >
                    <InputNumber step={1} max={8192} min={512} />
                  </Form.Item>

                  <Form.Item
                    label={
                      <Typography.Paragraph strong>
                        Max Distance
                      </Typography.Paragraph>
                    }
                    name='ssrMaxDistance'
                    initialValue={
                      postprocessingSettingsData?.ssrMaxDistance || 8
                    }
                  >
                    <InputNumber step={0.01} max={100} min={0} />
                  </Form.Item>
                </>
              )}

              <CameraConfig
                currentCameraConfig={currentCameraConfig}
                callBack={callBack}
                type={type}
                setDisableBtn={setDisableBtn}
              />

              {cloudConfig && (
                <CloudSettings
                  currentCloudConfig={cloudConfig}
                  callBack={handleCloudConfigChange}
                  type='cloud'
                  setDisableBtn={setDisableBtn}
                />
              )}

              {fogConfig && (
                <FogSettings
                  currentFogConfig={fogConfig}
                  callBack={handleFogConfigChange}
                  type='fog'
                  setDisableBtn={setDisableBtn}
                />
              )}
            </div>
          )}

          <div className={styles.btnWrapper}>
            <Button
              htmlType='submit'
              type='primary'
              className={styles.btn}
              disabled={loadingUpdate || disableBtn}
            >
              {loadingUpdate ? (
                <Spin
                  indicator={
                    <LoadingOutlined
                      style={{ fontSize: 30, color: '#fff' }}
                      spin
                    />
                  }
                ></Spin>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default GraphicsSettings;
